import { createFileRoute } from "@tanstack/react-router";
import { apiClient } from "@/lib/api-client";
import { EmbedStationPlayer } from "@/components/embed/EmbedStationPlayer";
import { EmbedDemoPlayer } from "@/components/embed/EmbedDemoPlayer";

// Validate theme parameter
function validateTheme(theme: string | undefined): "light" | "dark" {
	if (theme === "light" || theme === "dark") {
		return theme;
	}
	return "dark"; // Default to dark theme
}

export const Route = createFileRoute("/embed/$username/$stationSlug")({
	component: EmbedStationPage,
	validateSearch: (search: Record<string, unknown>) => {
		return {
			theme: validateTheme(search.theme as string | undefined),
		};
	},
	loader: async ({ params }) => {
		// Handle demo station as a special case
		if (params.username === "macrowave" && params.stationSlug === "demo") {
			return {
				station: {
					id: "station_demo00000000000000000000",
					name: "Apollo 11",
					slug: "demo",
					description: "Historic Apollo 11 mission audio - Demo Station",
					broadcasterId: "userdemo0000000000000000",
					broadcaster: {
						id: "userdemo0000000000000000",
						username: "macrowave",
						name: "Macrowave",
					},
					isActive: true,
					status: "live" as const,
					viewerCount: 0,
					nowPlaying: {
						track: "Apollo 11 Mission Audio",
						artist: "NASA",
						album: "Historic Spaceflight Recordings",
					},
					createdAt: new Date().toISOString(),
					lastActiveAt: new Date().toISOString(),
				},
				error: null,
			};
		}

		try {
			const station = await apiClient.users.getStationByUsernameAndSlug(
				params.username,
				params.stationSlug,
			);
			return { station, error: null };
		} catch (error) {
			return {
				station: null,
				error: error instanceof Error ? error.message : "Unknown error",
			};
		}
	},
	head: ({ params, loaderData }) => {
		const station = loaderData?.station;
		const { username, stationSlug } = params;

		const title = station
			? `${station.name} by @${station.broadcaster.username} - Macrowave`
			: `${stationSlug} - Macrowave`;

		const description = station
			? `Listen to ${station.name} by @${station.broadcaster.username} on Macrowave. ${station.description || "Live radio streaming."}`
			: `Listen to ${username}'s radio station on Macrowave`;

		const canonicalUrl = `https://macrowave.co/${username}/${stationSlug}`;

		return {
			meta: [
				{
					property: "og:title",
					content: title,
				},
				{
					property: "og:description",
					content: description,
				},
				{
					property: "og:type",
					content: "website",
				},
				{
					property: "og:url",
					content: canonicalUrl,
				},
				{
					name: "twitter:card",
					content: "summary_large_image",
				},
				{
					name: "twitter:title",
					content: title,
				},
				{
					name: "twitter:description",
					content: description,
				},
			],
		};
	},
});

function EmbedStationPage() {
	const { station, error } = Route.useLoaderData();
	const { theme } = Route.useSearch();

	const bgColor = theme === "dark" ? "bg-[#081506]" : "bg-gray-50";
	const textColor = theme === "dark" ? "text-[#6adf47]" : "text-gray-900";
	const mutedTextColor = theme === "dark" ? "text-[#4fa935]" : "text-gray-600";

	// Handle station not found
	if (error || !station) {
		return (
			<div
				className={`flex h-screen items-center justify-center ${bgColor} ${textColor} font-array p-4`}
			>
				<div className="text-center">
					<h1 className="text-lg font-bold mb-2">STATION NOT FOUND</h1>
					<p className={`text-sm ${mutedTextColor}`}>
						{error === "Station not found"
							? "This station doesn't exist."
							: "Error loading station."}
					</p>
				</div>
			</div>
		);
	}

	// Use demo player for the demo station
	const isDemo = station.slug === "demo";

	return (
		<div className={`h-screen ${bgColor}`}>
			{isDemo ? (
				<EmbedDemoPlayer station={station} theme={theme} />
			) : (
				<EmbedStationPlayer station={station} theme={theme} />
			)}
		</div>
	);
}
